import React, { useEffect, useRef, useState } from 'react';
import data from './Data.json';
import { Item } from './types';

const Guide = () => {
  const [selectedBtn, setSelectedBtn] = React.useState('Documentation');
  const [selectedId, setSelectedId] = React.useState(data[0].id);

  const rightPanelRef = React.useRef<HTMLDivElement | null>(null);
  const isAutoScrollingRef = React.useRef(false);
  const autoScrollTargetRef = React.useRef<string | null>(null);
  const autoScrollTimeoutRef = React.useRef<number | null>(null);

  // Store refs for each right-side section by id
  const itemRefs = React.useRef<Record<string, HTMLDivElement | null>>({});

  const baseBtn =
    'flex-1 w-full flex items-center justify-center text-[16px] px-4 py-2 rounded-lg transition-colors';
  const activeBtn = 'bg-[#FF0050] text-white';
  const inactiveBtn = 'bg-transparent hover:bg-[#FFE8EF]';

  // Left click -> scroll right
  const handleLeftClick = (id: string) => {
    setSelectedId(id);
    isAutoScrollingRef.current = true;
    autoScrollTargetRef.current = id;

    if (autoScrollTimeoutRef.current) {
      window.clearTimeout(autoScrollTimeoutRef.current);
    }

    // Keep observer from toggling the active item while smooth scroll is moving.
    autoScrollTimeoutRef.current = window.setTimeout(() => {
      isAutoScrollingRef.current = false;
      autoScrollTargetRef.current = null;
      autoScrollTimeoutRef.current = null;
    }, 900);

    const el = itemRefs.current[id];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Right scroll -> update left selected using IntersectionObserver
  React.useEffect(() => {
    const root = rightPanelRef.current;
    if (!root) return;

    const observer = new IntersectionObserver(
      (entries) => {
        // pick the most visible intersecting entry
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort(
            (a, b) => (b.intersectionRatio ?? 0) - (a.intersectionRatio ?? 0),
          )[0];

        if (visible?.target) {
          const id = (visible.target as HTMLElement).dataset.id;
          if (!id) return;

          if (isAutoScrollingRef.current) {
            if (id !== autoScrollTargetRef.current) return;

            isAutoScrollingRef.current = false;
            autoScrollTargetRef.current = null;

            if (autoScrollTimeoutRef.current) {
              window.clearTimeout(autoScrollTimeoutRef.current);
              autoScrollTimeoutRef.current = null;
            }
          }

          setSelectedId((currentId) => (currentId === id ? currentId : id));
        }
      },
      {
        root, // IMPORTANT: observe inside the scroll container (right panel)
        threshold: [0.25, 0.5, 0.75],
        rootMargin: '0px 0px -60% 0px', // treat "top area" as active zone
      },
    );

    // observe each section
    data.forEach((item: Item) => {
      const el = itemRefs.current[item.id];
      if (el) observer.observe(el);
    });

    return () => {
      observer.disconnect();

      if (autoScrollTimeoutRef.current) {
        window.clearTimeout(autoScrollTimeoutRef.current);
        autoScrollTimeoutRef.current = null;
      }
    };
  }, []);

  return (
    <div className='rounded-[24px] bg-white flex flex-col h-full min-h-0'>
      {/* Header */}
      <div className='p-6 flex h-[96px] items-center gap-6 shadow-[0_10px_20px_-15px_rgba(0,0,0,0.1)]'>
        <button
          className={`${baseBtn} ${
            selectedBtn === 'Documentation' ? activeBtn : inactiveBtn
          }`}
          onClick={() => setSelectedBtn('Documentation')}
        >
          Documentation
        </button>

        <button
          className={`${baseBtn} ${
            selectedBtn === 'Tutorials' ? activeBtn : inactiveBtn
          }`}
          onClick={() => setSelectedBtn('Tutorials')}
        >
          Tutorials
        </button>
      </div>

      {/* Content */}
      {selectedBtn === 'Documentation' ? (
        <div className='p-6 flex overflow-y-auto gap-6'>
          {/* Left list */}
          <div>
            <ul>
              {data.map((item: Item) => {
                const active = selectedId === item.id;
                return (
                  <li key={item.id}>
                    <button
                      type='button'
                      onClick={() => handleLeftClick(item.id)}
                      className={`w-[300px] text-left px-4 py-3 text-[14px] rounded-lg transition-colors
                      ${
                        active
                          ? 'bg-[#FFE5EE] text-[#FF0050] border-[#FF0050]'
                          : 'bg-white text-gray-800 border-gray-200 hover:bg-gray-50'
                      }
                    `}
                    >
                      {item.title}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Right scroll area */}
          <div
            ref={rightPanelRef}
            className='w-full space-y-10 border-l border-gray-200 pl-6 overflow-auto'
          >
            {data.map((item: Item) => (
              <div
                key={item.id}
                data-id={item.id}
                ref={(el) => {
                  itemRefs.current[item.id] = el;
                }}
                className='space-y-3'
              >
                <h1 className='pl-3 text-[28px] font-semibold text-gray-900'>
                  {item.title}
                </h1>

                <ul className='list-disc pl-8 space-y-1 text-[16px] text-gray-700'>
                  {item.description.map((text: string, idx: number) => (
                    <li key={`${item.id}-desc-${idx}`}>{text}</li>
                  ))}
                </ul>

                <img
                  src={item.image}
                  alt={item.title}
                  className='w-full h-auto'
                />
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className='p-6 flex overflow-y-auto gap-6'>
          {/* Video Tutorial */}
          <video
            controls
            className='w-full h-auto rounded-lg shadow-md'
          >
            <source src='/assets/guide-images/Funizzle-guide.mp4' type='video/mp4' />
            Your browser does not support the video tag.
          </video>
        </div>
      )}
    </div>
  );
};

export default Guide;
